<template>
  <div>
    <v-container pt-6 fluid v-resize="onResize">
      <v-row>
        <v-data-table
          :key="panelKey"
          item-key="idApuracao"
          :headers="headersApuracaoMensal"
          :items="apuracoes"
          :options.sync="pagination"
          :server-items-length="totalPage"
          :expanded.sync="expanded"
          class="elevation-0"
          :no-data-text="$t('label.tabela_sem_conteudo')"
          :footer-props="{
            itemsPerPageOptions: [10, 25, 50],
          }">
          <template v-slot:[`item.checkbox`]="{ item }">
            <v-checkbox
              v-model="item.selecionado"
              v-if="!statusAprovado(item)"
              hide-details
              class="shrink pt-0 mt-0"
              @click.stop="(e) => selecionarItem(item, e)">
            </v-checkbox>
          </template>
          <template v-slot:[`item.icon`]="{ item }">
            <v-tooltip bottom v-if="!statusAprovado(item)">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="orange darken-1"
                    dark
                    v-bind="attrs"
                    v-on="on"
                >
                  report_problem
                </v-icon>
              </template>
              <span>{{ $t('message.periodo_total_ou_parte_apurado') }}</span>
            </v-tooltip>
            <v-tooltip bottom v-if="statusAprovado(item)">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="deep-orange accent-4"
                    dark
                    v-bind="attrs"
                    v-on="on"
                >
                  info
                </v-icon>
              </template>
              <span>{{ $t('label.periodo_apurado') }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.acoes`]="{ item }">
            <v-menu bottom
              origin="center center"
              transition="scale-transition">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  :id="'more_vert_' + item.id"
                  icon>
                  <v-icon>more_vert</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  @click="detalharApuracao(item)">
                  <v-list-item-action>
                    <v-icon>details</v-icon>
                  </v-list-item-action>
                  <v-list-item-title>{{ $tc('label.detalhe', 2) }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="!statusAprovado(item)"
                  @click="abrirModalCancelamento(item)">
                  <v-list-item-action>
                    <v-icon>cancel</v-icon>
                  </v-list-item-action>
                  <v-list-item-title>{{ $tc('label.cancelar', 1) }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="!indCalculoSelloutVolume(item) || (indCalculoSelloutVolume(item) && !indBonificacaoFixo(item))"
                  @click="exportarNotas(item, false)">
                  <v-list-item-action>
                    <v-icon>get_app</v-icon>
                  </v-list-item-action>
                  <v-list-item-title>{{ $tc('label.exportar_nota', 2) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template v-slot:[`item.periodoVigencia`]="{ item }">
            {{ formatarVigencia(item.dataInicio)}}
          </template>
          <template v-slot:item.calculoBonificacao="{ item }">
            {{ item.calculoBonificacao ? $tc(`configuracao.CALCULO_BONIFICACAO.${item.calculoBonificacao}`) : '-' }}
          </template>
          <template v-slot:item.calculoApuracao="{ item }">
            {{ item.calculoApuracao ? $tc(`configuracao.METAS_BENEFICIOS.${item.calculoApuracao}`) : '-' }}
          </template>
          <template v-slot:[`item.qtdProduto`]="{ item }">
            <v-btn
              icon
              large
              @click="visualizarListaProdutos(item)"
              >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">visibility</v-icon>
                </template>
                <span v-if="item.qtdProduto > 0">
                  {{ item.qtdProduto }} {{ item.qtdProduto === 1 ? $tc('label.sku_selecionado', 1) : $tc('label.sku_selecionado', 2) }}
                </span>
                <span v-else> {{ $t('label.todos') }} </span>
              </v-tooltip>
            </v-btn>
          </template>
          <template v-slot:[`item.verba`]="{ item }">
            <tr v-if="item.indMetaIntervalo">
              <td>
                <span class="d-block">
                  {{ item.faixaAtingida ? item.formaBonificacao === 'FIXO' ?
                    getMoney(item.faixaAtingida) : getPercent(item.faixaAtingida) : $tc('label.sem_atingimento', 1)}}
                </span>
              </td>
              <td>
                <v-btn
                  icon
                  @click="isMetaCumulativa(item.tipoMetaApuracao) ? abrirModalVerbaIntervaloMetaCumulativa(item) : abrirModalIntervaloMeta(item)"
                  class="mx-2 text-left"
                >
                  <v-icon>
                    add_box
                  </v-icon>
                </v-btn>
              </td>
            </tr>
            <span v-else>
              {{ formatarVerba(item) }}
            </span>
          </template>
          <template v-slot:[`item.vlrRealizado`]="{ item }">
            <v-container fluid>
              <v-row>
                <v-col cols="12" v-if="indEdicaoRealizado(item)" :sm="indRealizadoDetalhamento(item) ? 8 : 12">
                  <v-text-field
                    v-if="realizadoPorQuantidade(item)"
                    :id="`vlrRealizado-${item.idApuracao}`"
                    :ref="`vlrRealizado-${item.idApuracao}`"
                    v-model="item.vlrRealizado"
                    :rules="[rules.required, rules.valorAjusteNegativo]"
                    :disabled="statusAprovado(item)"
                    type="Number"
                    style="min-width:80px"
                    :hint="String(item.vlrRealizadoOriginal !== 0 ? formatarValor(item.vlrRealizadoOriginal) : 0)"
                    @focus="guardarValorInicial(item)"
                    @change="alteracaoValorRealizado(item)"
                  ></v-text-field>
                  <input-money
                    v-else-if="item.calculoApuracao === 'SELLIN_VALOR'"
                    :id="`vlrRealizado-${item.idApuracao}`"
                    :ref="`vlrRealizado-${item.idApuracao}`"
                    v-model="item.vlrRealizado"
                    :rules="[rules.required]"
                    :disabled="statusAprovado(item)"
                    style="min-width:140px"
                    prefix="R$ "
                    :hint="String(item.vlrRealizadoOriginal !== 0 ? formatarValor(item.vlrRealizadoOriginal) : 0)"
                    @focus="guardarValorInicial(item)"
                    @change="alteracaoValorRealizado(item)"
                  ></input-money>
                  <v-text-field
                    :id="`vlrRealizado-${item.idApuracao}`"
                    :ref="`vlrRealizado-${item.idApuracao}`"
                    v-else
                    v-model="item.vlrRealizado"
                    :rules="[rules.required]"
                    :disabled="statusAprovado(item)"
                    type="Number"
                    style="min-width:80px"
                    suffix="%"
                    :hint="String(item.vlrRealizadoOriginal !== 0 ? formatarValor(item.vlrRealizadoOriginal) : 0)"
                    @focus="guardarValorInicial(item)"
                    @change="alteracaoValorRealizado(item)"
                  ></v-text-field>
                  <!-- <span v-if="item.vlrRealizado !== item.vlrRealizadoOriginal" class="grey--text caption">
                    <br/>
                    {{ formatarValorRealizado(item.vlrRealizadoOriginal, item.calculoApuracao) }}
                  </span> -->
                </v-col>
                <v-col class="mt-2" v-else cols="12" :sm="indRealizadoDetalhamento(item) ? 8 : 12">
                  <span>
                    {{ formatarValorRealizado(item.vlrRealizado, item.calculoApuracao, item.tipoMetaApuracao) }}
                    <v-btn
                      v-if="isMetaCumulativa(item.tipoMetaApuracao)"
                      icon
                      @click="abrirModalIntervaloMetaCumulativa(item)"
                      class="mx-2"
                    >
                      <v-icon>
                        info
                      </v-icon>
                    </v-btn>
                    <v-btn
                      v-else-if="indCalculoSelloutVolume(item) && item.vlrRealizado"
                      icon
                      @click="verificarQuantidadeRegristrosExportacao(item) && !indBonificacaoFixo(item)"
                      class="mx-2"
                    >
                      <v-icon>
                        get_app
                      </v-icon>
                    </v-btn>
                  </span>
                  <span v-if="item.vlrRealizado !== item.vlrRealizadoOriginal" class="grey--text caption">
                    <br/>
                    {{ formatarValorRealizado(item.vlrRealizadoOriginal, item.calculoApuracao, item.tipoMetaApuracao) }}
                  </span>
                </v-col>
                <v-col cols="12" sm="4" v-if="indRealizadoDetalhamento(item)">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        v-on="on"
                        @click="abrirModalRealizado(item.idApuracao)"
                        :class="indEdicaoRealizado(item) ? 'mx-2 mt-4' : ''"
                      >
                        <v-icon>
                          info
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $tc('label.detalhamento_realizado', 1) }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </template>
          <template v-slot:[`item.valorBase`]="{ item }">
            <dialog-edit-valor-base
              v-if="item.formaBonificacao !== 'FIXO'"
              :id="`valorBaseDefinido-${item.idApuracao}`"
              :ref="`valorBaseDefinido-${item.idApuracao}`"
              :item="item"
              :salvar-valor-alterado="salvarValorAlterado"
              :ind-valor-base-definido-alterado="indValorBaseDefinidoAlterado"
              @DialogEditValorBase__isEditandoValorBase="alterarIsEditandoValorBase"
            >
            </dialog-edit-valor-base>
            <span v-else> {{ $t('-') }} </span>
          </template>
          <template v-slot:[`item.vlrRetroativo`]="{ item }">
            {{ getMoney(item.vlrRetroativo) }}
            <tooltip-ajuda v-if="item.vlrRetroativo" color="black"
              :mensagem="`${$t('message.valor_retroatividade', { descricaoContratoAnterior: item.descricaoContratoAnterior})}`"
          />
          </template>
          <template v-slot:[`item.recebimentoDefinido`]="{ item }">
            <dialog-edit-recebimento-definido
              :id="`recDefinido-${item.idApuracao}`"
              :ref="`recDefinido-${item.idApuracao}`"
              :item="item"
              :salvar-valor-alterado="salvarValorAlterado"
              :ind-recebimento-definido-alterado="indRecebimentoDefinidoAlterado"
              :somente-leitura="isEditandoValorBase"
            >
            </dialog-edit-recebimento-definido>
          </template>
          <template v-slot:[`item.tipoUso`]="{ item }">
            <dialog-edit-tipo-uso
              :id="`tipoUso-${item.idApuracao}`"
              :ref="`tipoUso-${item.idApuracao}`"
              :item="item"
              :lista-tipos-uso="listaTiposUso"
              :salvar-tipo-uso-individual="salvarTipoUsoIndividual"
            >
            </dialog-edit-tipo-uso>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <apuracao-contrato-tabela-fornecedor
                :fornecedores="item.fornecedores">
              </apuracao-contrato-tabela-fornecedor>
            </td>
          </template>
        </v-data-table>
      </v-row>
    </v-container>
    <dialog-justificativa
      ref="dialogJustificativa"
      :justificativa="apuracaoSelecionada.justificativaAlterarRecebimento"
      @confirmar="confirmarAlteracaoValor"
      @cancelar="cancelarAlteracaoValor">
    </dialog-justificativa>

    <dialog-justificativa
      ref="dialogJustificativaValorBase"
      :justificativa="apuracaoSelecionada.justificativaAlterarValorBase"
      @confirmar="confirmarAlteracaoValorBase"
      @cancelar="cancelarAlteracaoValorBase">
    </dialog-justificativa>

    <dialog-realizado-detalhamento
      ref="dialogRealizadoDetalhamento">
    </dialog-realizado-detalhamento>

    <dialog-apuracao-contrato-meta-intervalo
      ref="dialogMetaIntervalo">
    </dialog-apuracao-contrato-meta-intervalo>

    <dialog-apuracao-contrato-cancelamento
      ref="dialogCancelamento"
      :confirmarCancelarApuracao="confirmarCancelarApuracao"
    >
    </dialog-apuracao-contrato-cancelamento>

    <v-dialog
      v-model="dialogPlanejamentoContrato"
      v-if="dialogPlanejamentoContrato"
      fullscreen
      hide-overlay
      persistent
      transition="dialog-bottom-transition">
      <v-card class="ModalPlanejamentoContrato">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="close()">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title><b>{{ $tc('title.planejamento_contrato', 1).toUpperCase()}}</b></v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <planejamento-contrato-form
            ref="modalForm"
            :somente-leitura="true"
            :id-contrato-fechamento="contrato.idContrato">
          </planejamento-contrato-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <planejamento-contrato-modal-selecao-produto
      ref="modalSelecaoProduto"
      :somente-leitura="true"
      :acao="acao"
      />

    <dialog-intervalo-meta-cumulativa
      ref="dialogIntervaloMetaCumulativa">
    </dialog-intervalo-meta-cumulativa>

    <dialog-verba-intervalo-meta-cumulativa
      ref="dialogVerbaIntervaloMetaCumulativa"
      :subtitulo="subTitulo">
    </dialog-verba-intervalo-meta-cumulativa>

  </div>
</template>

<script>

import ApuracaoContratoTabData from '@/spa/apuracao-contrato-samsung/ApuracaoContratoTabData';
import TooltipAjuda from '@/shared-components/TooltipAjuda';

export default {
  name: 'ApuracaoContratoTabMensal',
  mixins: [
    ApuracaoContratoTabData,
  ],
  components: {
    TooltipAjuda,
  },
  props: {
    apuracoes: {
      type: Array,
      default: () => ([]),
    },
    totalPage: {
      type: Number,
      default: 0,
    },
    montarDataInicioFimEdicaoValores: {
      type: Function,
    },
    listaTiposUso: {
      type: Array,
      default: () => ([]),
    },
    filtros: {
      type: Object,
    },
    selecionarItem: Function,
  },
  data() {
    return {
      pagination: {
        itemsPerPage: 10,
        page: 1,
      },
      lastPagination: {},
      headersApuracaoMensal: [
        { text: '', value: 'checkbox', sortable: false },
        { text: '', value: 'icon', sortable: false },
        {
          text: this.$tc('label.acao', 2), value: 'acoes', class: 'nowrap', sortable: false,
        },
        {
          text: this.$tc('label.cod_contrato', 1), value: 'idContrato', class: 'nowrap',
        },
        {
          text: this.$tc('label.fornecedor', 1), value: 'origemContrato', class: 'nowrap',
        },
        {
          text: this.$tc('label.cod_apuracao', 1), value: 'idApuracao', class: 'nowrap',
        },
        { text: this.$tc('title.categorias', 1), value: 'categoria', class: 'nowrap' },
        { text: this.$tc('title.produto', 1), value: 'qtdProduto', class: 'nowrap' },
        {
          text: this.$tc('label.periodo_vigencia', 1), value: 'periodoVigencia', class: 'nowrap',
        },
        {
          text: this.$tc('label.calculo_apuracao', 1), value: 'calculoApuracao', class: 'nowrap',
        },
        {
          text: this.$tc('label.calculo_bonificacao', 1), value: 'calculoBonificacao', class: 'nowrap',
        },
        {
          text: this.$tc('label.verba', 1), value: 'verba', class: 'nowrap', cellClass: 'nowrap',
        },
        {
          text: this.$tc('label.realizado', 1), value: 'vlrRealizado', class: 'nowrap', cellClass: 'nowrap',
        },
        {
          text: this.$tc('label.valor_base', 1), value: 'valorBase', class: 'nowrap', cellClass: 'nowrap',
        },
        {
          text: this.$tc('label.ajuste_contratual', 1), width: '10%', value: 'vlrRetroativo', class: 'nowrap', cellClass: 'nowrap',
        },
        {
          text: this.$tc('label.recebimento_definido', 1), value: 'recebimentoDefinido', cellClass: 'nowrap', class: 'nowrap',
        },
        {
          text: this.$tc('label.tipo_uso_definido', 1), value: 'tipoUso', width: '10%', class: 'nowrap', cellClass: 'nowrap', sortable: false,
        },
        { text: '', value: 'data-table-expand' },
      ],
    };
  },
  computed: {
  },
  watch: {
    pagination: {
      handler(pagination) {
        if (this.lastPagination.page === null || typeof this.lastPagination.page === 'undefined') {
          this.lastPagination = pagination;
          return;
        }
        if (this.mesmaPagina(pagination, this.lastPagination) && !this.ordenacaoAlterada(pagination, this.lastPagination)) {
          return;
        }
        this.resetarExpanded();
        this.lastPagination = pagination;
        this.$emit('ApuracaoContratoTabMensal__AplicaFiltros', this.pagination);
      },
      deep: true,
    },
    expanded(apuracao) {
      this.verificarApuracaoExpandida(apuracao);
    },
  },
  methods: {
  },
};
</script>
<style lang="scss">
  .ModalPlanejamentoContrato {
    background-color: #EEEEEE !important;
  }
</style>
